import { useJvvnlFeederChartData } from "./useJvvnlFeederChartData";
import { JvvnlEntitySearchBar } from "./JvvnlEntitySearchBar";
import type { EmbeddedNet, NetVariable, DateRange } from "./types";
import { ChartAreaLoader } from "../../../../components/Loading";
import { FilterTab } from "../../../../components/Filters";
import { DataTabs } from "../../../Rajasthan/Demand/Data";
import DownloadButton from "../../../../components/DownloadButton";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../../components/table/SummaryTable";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { CriticalPeriodsToggle } from "../../../../components/CriticalPeriodsToggle";
import { SearchBox } from "../../../../components/Search";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { EmptyState } from "../../../../components/recharts/ReMixCharts/components/EmptyState";
import { renderHourTick } from "../../../../components/recharts/components";
import {
  NET_CONSUMPTION_CIRCLE,
  pickLastSingle,
  isolateOnAllSelected,
} from "./constants";

export function FeederChartPopup(props: {
  embedded: EmbeddedNet;
  tooltipBoundary?: "viewport" | "chart";
  emptyStateHeight?: string | number;
  weatherParam?: string | null;
  netVariable?: NetVariable | "Actual" | "Forecast" | "Weather" | null;
  variablesOverride?: string[] | null;
  forecastVariants?: string[] | null;
  actualVariants?: string[] | null;
  dateRangeOverride?: DateRange | null;
  forecastDateOverride?: string | null;
  onEmbeddedFeederChange?: (f: { id: number; name: string } | null) => void;
  onEmbeddedFeederListChange?: (fs: { id: number; name: string }[]) => void;
  onEmbeddedNetChange?: (
    t: {
      level: "discom_sub" | "transco" | "circle";
      id: number | string | undefined;
      name: string;
    } | null,
  ) => void;
}) {
  const { state, setters, derived } = useJvvnlFeederChartData({
    kind: "popup",
    ...props,
  });

  const {
    loading,
    embeddedView,
    selectedBlock,
    selectedInterval,
    netCircle,
    netTransco,
    netDiscom,
    selectedTranscoForDiscom,
    selectedDiscomSubstations,
    selectedFeederOptions,
    selectedFeederSubstations,
    discomSubstations,
    circleOptions,
    selectedCircle,
    selectedFeederTransco,
    selectedSubstationOptions,
    selectedPlantTypeOptions,
    selectedPlantOptions,
    selectedGenCircle,
    allPlants,
    feederOpenSignal,
    selectedChartType,
    selectedFunction,
    selectedUnit,
    selectedVariables,
    chartData,
    criticalPeriodKinds,
  } = state;
  const {
    setEmbeddedView,
    setSelectedBlock,
    setCpActive,
    setNetCircle,
    setNetTransco,
    setNetDiscom,
    setSelectedTranscoForDiscom,
    setSelectedDiscomSubstations,
    setSelectedFeederSubstations,
    setSelectedFeederOptions,
    setSelectedCircle,
    setSelectedFeederTransco,
    setSelectedSubstationOptions,
    setSelectedPlantTypeOptions,
    setSelectedPlantOptions,
    setSelectedGenCircle,
    setCriticalPeriodKinds,
  } = setters;
  const {
    tablePar,
    cpActive,
    hierSepClass,
    noCircleSelected,
    netCircleOptions,
    netTranscoOptions,
    netDiscomOptions,
    transcoOptionsForDiscom,
    filteredDiscomSubstationOptions,
    filteredFeederOptions,
    feederSubstationOptions,
    substationOptions,
    plantOptions,
    plantTypeOptions,
    blockDurationMinutes,
    xDataKey,
    secondDataKey,
    legends,
    handleLegendSelect,
    selectedLegends,
    selectedWeatherMetricLabel,
  } = derived;
  const { emptyStateHeight, onEmbeddedFeederChange } = props;

  const { embedded } = props;
  const isCircle = embedded.level === "circle";
  const isTransco = embedded.level === "transco";
  const isDiscomSub = embedded.level === "discom_sub";
  const isFeeder = embedded.level === "feeder";
  const isPlant = embedded.level === "plant";
  // Levels that render the Circle→Transco→Discom selector row.
  const showNetRow = isCircle || isTransco || isDiscomSub || isFeeder;
  return (
    <div className="flex flex-col gap-3 p-1 relative">
      <div className="gas-chart-container">
        {loading ? (
          <ChartAreaLoader />
        ) : (
          <div className="flex flex-col justify-between border-gray-200 bg-white w-full">
            <div
              className="gas-heading w-full"
              style={{
                flexDirection: "column",
                alignItems: "stretch",
                gap: "12px",
              }}
            >
              <div className="flex items-center justify-between gap-x-10 w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                <h2 className="chartHeading w-60">
                  {isPlant
                    ? "Generation"
                    : isFeeder
                      ? "Feeder Load"
                      : "Net Consumption"}
                </h2>
                <div className="flex items-center gap-x-3">
                  <FilterTab
                    options={DataTabs.map((item) => item.label)}
                    selected={[embeddedView]}
                    onClick={(opt) =>
                      setEmbeddedView(opt as "Chart" | "Summary" | "Table")
                    }
                    variant="pill-outline"
                  />
                  {/* <DownloadButton
                    onClick={() =>
                      [
                        downloadChartTableCSV,
                        downloadSummaryCSV,
                        downloadChartTableCSV,
                      ][
                        ["Chart", "Summary", "Table"].indexOf(embeddedView)
                      ]?.(tablePar)
                    }
                  /> */}
                </div>
              </div>
              {(showNetRow || isPlant) && (
                <div className="flex flex-wrap gap-3 items-center border-b border-gray-200 pb-3">
                  <JvvnlEntitySearchBar
                    show={{
                      circle: false,
                      substation: false,
                      discomSubstation: false,
                      feeder: isFeeder,
                      generation: isPlant,
                      netConsumption: isCircle || isTransco || isDiscomSub,
                    }}
                    netLevels={{
                      circle: isCircle || isTransco || isDiscomSub,
                      transco: isTransco || isDiscomSub,
                      discom: isDiscomSub,
                    }}
                    hierSepClass={hierSepClass}
                    circleOptions={circleOptions}
                    selectedCircle={selectedCircle}
                    setSelectedCircle={setSelectedCircle}
                    selectedFeederTransco={selectedFeederTransco}
                    setSelectedFeederTransco={setSelectedFeederTransco}
                    setSelectedFeederSubstations={setSelectedFeederSubstations}
                    selectedFeederSubstations={selectedFeederSubstations}
                    feederSubstationOptions={feederSubstationOptions}
                    transcoOptionsForDiscom={transcoOptionsForDiscom}
                    filteredFeederOptions={filteredFeederOptions}
                    selectedFeederOptions={selectedFeederOptions}
                    setSelectedFeederOptions={setSelectedFeederOptions}
                    feederOpenSignal={feederOpenSignal}
                    substationOptions={substationOptions}
                    selectedSubstationOptions={selectedSubstationOptions}
                    setSelectedSubstationOptions={setSelectedSubstationOptions}
                    selectedTranscoForDiscom={selectedTranscoForDiscom}
                    setSelectedTranscoForDiscom={setSelectedTranscoForDiscom}
                    filteredDiscomSubstationOptions={
                      filteredDiscomSubstationOptions
                    }
                    selectedDiscomSubstations={selectedDiscomSubstations}
                    setSelectedDiscomSubstations={setSelectedDiscomSubstations}
                    netCircleOptions={netCircleOptions}
                    netCircle={netCircle}
                    setNetCircle={setNetCircle}
                    netTranscoOptions={netTranscoOptions}
                    netTransco={netTransco}
                    setNetTransco={setNetTransco}
                    netDiscomOptions={netDiscomOptions}
                    netDiscom={netDiscom}
                    setNetDiscom={setNetDiscom}
                    discomSubstations={discomSubstations}
                    genCircleOptions={circleOptions}
                    selectedGenCircle={selectedGenCircle}
                    setSelectedGenCircle={setSelectedGenCircle}
                    plantTypeOptions={plantTypeOptions}
                    selectedPlantTypeOptions={selectedPlantTypeOptions}
                    setSelectedPlantTypeOptions={setSelectedPlantTypeOptions}
                    plantOptions={plantOptions}
                    selectedPlantOptions={selectedPlantOptions}
                    setSelectedPlantOptions={setSelectedPlantOptions}
                    allPlants={allPlants}
                  />
                  <div className="flex items-center gap-x-2 shrink-0 self-center ml-auto">
                    <BlockRangeSelector
                      blocks={Array.from({ length: 48 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((it) => it.value)}
                      setSelectedOptions={setSelectedBlock as any}
                      blockMinutes={30}
                    />
                    {selectedInterval.isTime && (
                      <>
                        <span className="text-gray-300 select-none ml-3 self-center">
                          {" "}
                          |
                        </span>
                        <CriticalPeriodsToggle
                          active={cpActive}
                          onToggle={setCpActive}
                          disabled={selectedBlock.length > 0}
                          disabledReason="Unavailable while specific blocks are selected. Clear block selection to view critical periods."
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            {noCircleSelected || chartData.length === 0 ? (
              <div
                className={`relative min-h-[300px]`}
                style={{ height: emptyStateHeight }}
              >
                <EmptyState />
              </div>
            ) : (
              <div className="jvvnl-embedded-view flex-1 min-h-0 flex flex-col">
                {embeddedView === "Chart" && (
                  <ReMixChart
                    key={`jvvnl-embedded-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}-${selectedFunction}`}
                    data={chartData}
                    showBrush
                    brushIndex={{
                      startIndex: 0,
                      endIndex: Math.max(0, chartData.length - 1),
                    }}
                    isTimeSlot={selectedInterval.isTime}
                    blockDurationMinutes={blockDurationMinutes}
                    onlyTitle
                    barCategoryGap={10}
                    unit={selectedUnit}
                    yAxisLabel={selectedUnit}
                    secondYAxisLabel={
                      selectedVariables.includes("Weather")
                        ? selectedWeatherMetricLabel
                        : undefined
                    }
                    legends={legends}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    xDataKey={xDataKey}
                    xTick={
                      selectedInterval.isTime && selectedBlock.length === 0
                        ? (v: any) =>
                            renderHourTick(
                              v,
                              10,
                              blockDurationMinutes === 30 ? 2 : 4,
                            )
                        : undefined
                    }
                    secondXDataKey={secondDataKey()}
                    showCriticalPeriods={selectedBlock.length === 0}
                    hideCriticalPeriodsButton={true}
                    activePeriodKinds={criticalPeriodKinds}
                    onActivePeriodKindsChange={setCriticalPeriodKinds}
                  />
                )}
                {embeddedView === "Summary" && (
                  <LegendSummaryTable {...tablePar} disableCopy />
                )}
                {embeddedView === "Table" && (
                  <ChartTable
                    {...tablePar}
                    hideCriticalPeriodsButton={true}
                    cpActive={cpActive}
                    onCpActiveChange={setCpActive}
                    disableCopy
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
